/**
 * Multiplayer entry points used by main.ts:
 *
 *   const save = new WorldSave(SEED);
 *   prepareNetSave(save);   // point the save at this page's room namespace
 *   ...
 *   const net = installNet({...});   // title Play, Multiplayer screen, chat, session, hooks
 *   net.frame();                     // per frame, before rendering
 *
 * There is one shared world. The title's Play button joins this page's room:
 * the fixed code `MAINV<worldgen version>`, or `?room=CODE` for a private
 * one. Peers race to host it (auto mode: join whoever answers within
 * `discoverMs`, otherwise host alone), and every page lives in that room's
 * save namespace (`opencraft.mp.v<wgv>.<code>`), which doubles as this peer's
 * cache of the world: whoever is alone hosts it, whoever joins gets the
 * host's snapshot. Offline (no signaling) falls back to a private
 * in-memory link, so Play always lands you in the world.
 */

import type * as THREE from 'three';
import type { WorldSave } from '../world/save';
import type { ChunkManager } from '../world/manager';
import type { Player } from '../player/player';
import type { EntityManager } from '../entities/manager';
import type { EntityHost } from '../entities/host';
import { effectiveLight } from '../entities/host';
import type { EnvUniforms } from '../render/env';
import type { MenuHost } from '../ui/menus';
import { SEED } from '../constants';
import { WORLDGEN_VERSION } from '../worldgen/terrain';
import { getChatOverlay } from '../ui/chat';
import { MultiplayerScreen, type MpController } from '../ui/screens/multiplayer';
import { GameBridge, type GameDeps } from './game';
import { loadOrCreateIdentity, type Identity } from './identity';
import { signalingUrl } from './ice';
import { RemotePlayers } from './remotePlayers';
import { NetSession, type MobUseFx, type Status } from './session';
import type { Mob } from '../entities/mob';
import type { Stack } from '../game/types';
import { listRoomCaches, loadRoomCache, saveRoomCache } from './store';
import { workerTransport } from './signaling/workerTransport';
import { sanitizeName } from './validate';
import { WorldHttp, WorldLink, bootWorld, worldServerBase, type BootResult } from './worldServer';
import {
  OFFLINE_DISCOVER_MS, ROOM_DISCOVER_MS, bindPlayerState, clearSaveEdits, clearSaveMemory, connectTransport,
  ensureRoomSave, mainRoomCode, normalizeRoomCode, playerNameFromKey, playerSaveKey, roomFromSearch,
  playerUpload, roomSaveKey as roomSaveKeyFor, saveBootTarget, type SaveInternals, type SaveWorldTarget, type TransportCandidate,
} from './room';

const PENDING_KEY = 'opencraft.net.pending';
const NAME_KEY = 'opencraft.net.name';
const TAB_ID_KEY = 'opencraft.net.tabid';
/** How long the save waits for this tab's identity before playing without it. */
const IDENTITY_TIMEOUT_MS = 3000;

interface Boot { mode: 'join' | 'host'; code: string; name: string }
let boot: Boot | null = null;
/** The room this page's save and session belong to (see prepareNetSave). */
let pageRoomCode = '';
/** This tab's key namespace for its own inventory/hotbar/stats/position. */
let pagePlayerKey = '';
let hadSavedPosition = false;
/**
 * The room's world server (docs/world-server.md). `worldBoot` is null when none
 * is configured (offline solo world); otherwise it says whether this page plays
 * on the server's world or, with the server unreachable, offline on its own.
 * `pageLink` is the socket the session sends its block edits through.
 */
let worldBoot: BootResult | null = null;
let pageLink: WorldLink | null = null;
/** How often a changed player save is uploaded. */
const PLAYER_UPLOAD_MS = 5000;
/** Name derived from the identity key, filled in once the key is loaded. */
let keyName = '';

/** The room this page belongs to: a pending join/host, else `?room=`, else the main room. */
export function roomOfPage(): string {
  return pageRoomCode || roomFromSearch(location.search) || mainRoomCode(WORLDGEN_VERSION);
}

/** Save namespace of a room (kept exported for tools and tests). */
export function roomSaveKey(code: string): string { return roomSaveKeyFor(code, WORLDGEN_VERSION); }

/** Read (and consume) a join/re-host left behind by the Multiplayer screen. */
function readPending(): Boot | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_KEY);
    if (raw) sessionStorage.removeItem(PENDING_KEY);
  } catch { return null; }
  if (!raw) {
    // `?play=1` skips the title: join the page's room (?room=, else the main
    // one) as if Play had been clicked. Used by the agent API (agent-api/).
    try {
      if (new URLSearchParams(location.search).get('play') === '1') {
        return { mode: 'join', code: roomFromSearch(location.search) ?? mainRoomCode(WORLDGEN_VERSION), name: '' };
      }
    } catch { /* ignore */ }
    return null;
  }
  try {
    const b = JSON.parse(raw) as Boot;
    const code = normalizeRoomCode(b?.code);
    if (!code || (b.mode !== 'join' && b.mode !== 'host')) return null;
    return { mode: b.mode, code, name: typeof b.name === 'string' ? b.name : '' };
  } catch { return null; }
}

/**
 * Call right after `new WorldSave(SEED)`. Every page plays in a room, so the
 * save is pointed at that room's namespace (item 3); on a room's first run the
 * old single-player save is copied into it, which is how an existing single
 * player world becomes the shared one.
 *
 * The room file holds the *world*. The player's own half (position, hotbar,
 * inventory, stats) is loaded from this tab's identity namespace instead, so a
 * second tab is a clean player with its own key rather than a clone of whoever
 * wrote the room file last. Everything is mirrored back on flush.
 */
export async function prepareNetSave(save: WorldSave): Promise<void> {
  boot = readPending();
  pageRoomCode = boot?.code ?? roomFromSearch(location.search) ?? mainRoomCode(WORLDGEN_VERSION);
  const s = save as unknown as SaveInternals;
  if (typeof s.load !== 'function') { console.warn('[net] WorldSave layout changed'); return; }
  const spKey = s.storageKey ?? '';
  const roomKey = roomSaveKey(pageRoomCode);
  let adopted = false;
  try { adopted = ensureRoomSave(localStorage, spKey, roomKey); } catch { /* storage unavailable */ }
  s.storageKey = roomKey;
  clearSaveMemory(s); // drop what the single-player load() just put in memory
  try {
    s.load();
  } catch {
    console.warn('[net] room save unreadable; starting from a fresh world');
  }

  pagePlayerKey = await resolvePlayerKey(pageRoomCode);
  const bound = bindPlayerState(save as unknown as SaveInternals & { flush(force?: boolean): void }, localStorage, roomKey, pagePlayerKey);
  console.info(
    `[net] room ${pageRoomCode}, save ${roomKey}${adopted ? ' (adopted the single-player world)' : ''}, ` +
    `player ${pagePlayerKey.slice(-17)}${bound.adopted ? ' (adopted the room player)' : bound.clean ? ' (clean player)' : ''}`);

  // the world server owns the room's blocks and this identity's player: make
  // the save a copy of both before anything reads it
  await connectWorld(pageRoomCode, save);
  hadSavedPosition = save.player !== null;
}

/**
 * Connect to the room's world server and make the local save a copy of its
 * world, before anything generates chunks. Bounded (a few seconds for the
 * handshake, longer only while a world is actually downloading). When the
 * server cannot be reached the page plays offline on its local copy.
 */
async function connectWorld(code: string, save: WorldSave): Promise<void> {
  const base = worldServerBase();
  if (!base) return;
  const offline = (detail: string): void => {
    worldBoot = { mode: 'offline', applied: 0, imported: 0, player: false, detail };
    console.warn(`[net] world server: ${detail}; playing offline (nothing is shared or saved to the room)`);
  };
  let id: Identity;
  try {
    id = await withTimeout(pageIdentity(), IDENTITY_TIMEOUT_MS);
  } catch {
    offline('no signing identity in this browser');
    return;
  }
  const link = new WorldLink({ base, code, identity: id });
  const internals = save as unknown as SaveWorldTarget;
  // What this browser last got through to the server. If the local player
  // differs from it, the tab closed (or reloaded) with changes the server never
  // received: the local copy is the newer one and must not be rolled back.
  const sentKey = pagePlayerKey + '.sent';
  const textOf = (): string => { try { const d = playerUpload(internals); return d ? JSON.stringify(d) : ''; } catch { return ''; } };
  let sent = '';
  try { sent = localStorage.getItem(sentKey) ?? ''; } catch { /* storage unavailable */ }
  const localText = textOf();
  const localAhead = localText !== '' && sent !== '' && localText !== sent;
  const target = saveBootTarget(internals);
  const setPlayer = target.setPlayer;
  target.setPlayer = (p) => { if (!localAhead) setPlayer(p); };
  try {
    worldBoot = await bootWorld({ link, http: new WorldHttp(base), code, wgv: WORLDGEN_VERSION, seed: SEED, target });
  } catch (e) {
    link.disable();
    offline(e instanceof Error ? e.message : String(e));
    return;
  }
  if (worldBoot.mode !== 'server') {
    console.warn(`[net] world server: ${worldBoot.detail}; playing offline (nothing is shared or saved to the room)`);
    return;
  }
  pageLink = link;
  console.info(`[net] room ${code}: world from the server (${worldBoot.applied} block(s) applied, ` +
    `${worldBoot.imported} uploaded${worldBoot.player ? (localAhead ? ', local player is newer: kept' : ', player restored') : ''})`);
  if (!localAhead) sent = textOf();

  // the player half of the save follows the identity to the server
  const flush = save.flush.bind(save);
  let last = Number.NEGATIVE_INFINITY;
  save.flush = (force = false): void => {
    flush(force);
    const now = performance.now();
    if (!link.live || (!force && now - last < PLAYER_UPLOAD_MS)) return;
    const data = playerUpload(internals);
    if (!data) return;
    let text: string;
    try { text = JSON.stringify(data); } catch { return; }
    if (text === sent) return;
    if (!link.savePlayer(data)) return;
    sent = text;
    last = now;
    try { localStorage.setItem(sentKey, text); } catch { /* storage full: worst case the server copy wins next boot */ }
  };
}

/** Per-tab id used when the identity key cannot be loaded (private mode, ...). */
function tabFallbackId(): string {
  try {
    const v = sessionStorage.getItem(TAB_ID_KEY);
    if (v) return v;
    const id = 't' + Math.random().toString(36).slice(2, 10);
    sessionStorage.setItem(TAB_ID_KEY, id);
    return id;
  } catch {
    return 't' + Math.random().toString(36).slice(2, 10);
  }
}

/**
 * Where this tab keeps its own player state. Resolved before the save loads
 * (it decides inventory/hotbar/stats), from this tab's identity slot - which
 * is per tab, so a second tab is a second player.
 */
async function resolvePlayerKey(code: string): Promise<string> {
  try {
    const id = await withTimeout(pageIdentity(), IDENTITY_TIMEOUT_MS);
    return playerSaveKey(code, WORLDGEN_VERSION, id.id);
  } catch (e) {
    console.warn('[net] identity unavailable (' + (e instanceof Error ? e.message : String(e)) + '); playing as an unnamed player');
    return playerSaveKey(code, WORLDGEN_VERSION, tabFallbackId());
  }
}

/** Background-tab-proof 20 Hz timer (worker timers aren't throttled like window ones). */
function workerTimer(cb: () => void, ms: number): () => void {
  try {
    const url = URL.createObjectURL(new Blob([`setInterval(()=>postMessage(0),${ms | 0});`], { type: 'text/javascript' }));
    const w = new Worker(url);
    w.onmessage = () => cb();
    return () => { w.terminate(); URL.revokeObjectURL(url); };
  } catch {
    const h = setInterval(cb, ms);
    return () => clearInterval(h);
  }
}

/** One identity per open tab (two tabs of one browser are two players). */
async function identitySlot(): Promise<string> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks) {
    // No Web Locks outside a secure context (a plain-http lan origin, say), so
    // fall back to a slot of this tab's own: sessionStorage starts empty in
    // every tab and survives a reload, so a tab keeps its player either way.
    const KEY = 'opencraft.net.slot';
    try {
      const kept = sessionStorage.getItem(KEY);
      if (kept) return kept;
      const made = 'tab' + Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem(KEY, made);
      return made;
    } catch { return 'tab' + Math.random().toString(36).slice(2, 10); }
  }
  for (let i = 0; i < 8; i++) {
    const name = i === 0 ? 'default' : 'tab' + i;
    const got = await new Promise<boolean>((res) => {
      void locks.request('opencraft-identity-' + name, { ifAvailable: true }, (lock) => {
        if (!lock) { res(false); return undefined; }
        res(true);
        return new Promise<void>(() => {}); // hold for the page's lifetime
      });
    });
    if (got) return name;
  }
  return 'tab' + Math.floor(Math.random() * 1e9);
}

/**
 * This tab's signing identity, loaded once per page and shared by the save
 * bootstrap and the session. Asking identitySlot() twice would hand the same
 * tab a *second* key: its lock is held for the page's lifetime.
 */
let pageIdentityPromise: Promise<Identity> | null = null;
function pageIdentity(): Promise<Identity> {
  if (!pageIdentityPromise) {
    pageIdentityPromise = identitySlot()
      .then((slot) => loadOrCreateIdentity(slot))
      .catch((e: unknown) => { pageIdentityPromise = null; throw e; });
  }
  return pageIdentityPromise;
}

/** Resolve within `ms`, so a stuck storage/lock API cannot hold up the boot. */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`timed out after ${ms}ms`)), ms);
    p.then((v) => { clearTimeout(timer); resolve(v); }, (e) => { clearTimeout(timer); reject(e); });
  });
}

export interface InstallDeps {
  seed: number;
  chunks: ChunkManager;
  save: WorldSave;
  player: Player;
  entities: EntityManager;
  entityHost: EntityHost;
  sky: { time: number };
  scene: THREE.Scene;
  env: EnvUniforms;
  menus: MenuHost;
  canvas: HTMLElement;
  health: () => number;
  dead: () => boolean;
  guiScale: () => number;
  /** A chat line that arrived from another player (peers' commands are replayed here). */
  onPeerChat?: (name: string, text: string) => void;
  /** Host: run a remote player's right-click on a mob (see GameDeps.useOnMob). */
  useOnMob?: GameDeps['useOnMob'];
  /** Client: the host ran our right-click `r`; apply `fx` to the inventory. */
  onMobUsed?: (r: number, fx: MobUseFx) => void;
}

export interface NetController {
  frame(): void;
  readonly session: NetSession | null;
  /**
   * Right-click on a mob that another peer simulates: ask that peer to run it.
   * Returns the request id (answered through `onMobUsed`), or null when the mob
   * is ours and the caller should just run the interaction itself.
   */
  useOnMob(mob: Mob, held: Stack): number | null;
}

/**
 * GameBridge for the shared world: the host's snapshot *replaces* what we
 * hold, so drop our divergent cached edits (and the loaded chunks, which still
 * show them) before importing it. Otherwise stale local-only edits in
 * already-generated chunks survive the sync (handoff item 4).
 */
class SharedWorldBridge extends GameBridge {
  constructor(
    deps: GameDeps,
    private readonly save: WorldSave,
    private readonly chunks: ChunkManager,
    private readonly onReset: (dropped: number) => void,
  ) { super(deps); }

  override importDelta(q: Int32Array): void {
    const dropped = clearSaveEdits(this.save as unknown as SaveInternals);
    if (dropped > 0) {
      // drop the loaded chunks: they still hold the edits we just forgot
      this.chunks.setDimension(this.chunks.dimension);
      this.onReset(dropped);
    }
    super.importDelta(q);
  }
}

export function installNet(d: InstallDeps): NetController {
  const room = roomOfPage();
  // one chat overlay per page (ui/chat.ts shares it with single player)
  const chat = getChatOverlay(document.body, () => Math.max(1, Math.min(3, Math.floor(innerWidth / 640) + 1)));
  const bridge = new SharedWorldBridge({
    seed: d.seed, chunks: d.chunks, save: d.save, player: d.player, entities: d.entities, entityHost: d.entityHost,
    sky: d.sky, health: d.health, dead: d.dead, hadSavedPosition, useOnMob: d.useOnMob,
  }, d.save, d.chunks, (n) => chat.add('', `World synced (${n} local edit${n === 1 ? '' : 's'} replaced)`, true));
  const remote = new RemotePlayers(d.scene, d.env, (x, y, z) => effectiveLight(d.entityHost, x, y, z));
  let session: NetSession | null = null;
  let code = room;
  let transportLabel = 'connecting...';
  let statusText = '';
  let stopTimer: (() => void) | null = null;
  let cacheTimer = 0;
  let opening = false;
  let tabHeld = false;
  let listSig = '';
  let listAt = 0;

  // ------------------------------------------------------------------ names

  const readName = (): string | null => {
    try { const n = localStorage.getItem(NAME_KEY); const t = (n ?? '').trim(); return t ? t.slice(0, 20) : null; } catch { return null; }
  };
  const rememberName = (n: string): string => {
    const raw = (n ?? '').trim();
    if (!raw) return d.menus.playerName; // an empty field keeps the name you had
    const t = sanitizeName(raw);
    if (!t) return d.menus.playerName;
    try { localStorage.setItem(NAME_KEY, t); } catch { /* ignore */ }
    if (d.menus.playerName !== t) d.menus.playerName = t;
    // a live session holds the name it joined with; push the change through it
    // so the player list, the chat lines and the nametags all follow along
    session?.setName(t);
    return t;
  };
  d.menus.onPlayerName = rememberName;
  d.menus.playerName = readName() ?? '';

  /**
   * The name to play under: the Options entry if set, else "Player####" from
   * this tab's identity (which prepareNetSave already loaded for the save).
   */
  function whoami(): Promise<Identity> {
    return pageIdentity().then((id) => {
      keyName = playerNameFromKey(id.id);
      d.menus.playerName = readName() ?? keyName;
      return id;
    });
  }

  // ----------------------------------------------------------------- status

  const STATUS_LABEL = {
    connecting: 'Connecting...', syncing: 'Joining world...', live: 'Connected',
    migrating: 'Reconnecting...', closed: 'Disconnected',
  } as const satisfies Record<Status, string>;

  const onStatus = (s: Status, detail?: string): void => {
    statusText = detail ?? STATUS_LABEL[s];
    if (s === 'closed' && detail) chat.add('', detail, true);
    refreshStatus();
  };

  /**
   * With a world server nobody hosts the world, so the status says "Online";
   * "Hosting" / "Joined" only describe the offline solo model.
   */
  const roleLabel = (_s: NetSession): string => 'Online';

  /** The one line that must be readable over the menus: Online / Hosting / Joined (N). */
  function refreshStatus(): void {
    if (!session || session.status === 'closed') { chat.setStatus(null); return; }
    const n = session.memberList().length;
    chat.setStatus(session.status === 'live' ? `${roleLabel(session)} (${n})` : statusText);
  }

  // ------------------------------------------------------------- the session

  function transportCandidates(roomCode: string): TransportCandidate[] {
    const out: TransportCandidate[] = [];
    const sig = signalingUrl();
    if (sig) {
      out.push({
        label: 'Cloudflare Worker', ms: 5000,
        make: () => workerTransport(sig, roomCode, (e) => chat.add('', e, true)),
      });
    }
    return out;
  }

  function startCacheTimer(s: NetSession): void {
    clearInterval(cacheTimer);
    cacheTimer = window.setInterval(() => {
      if (session?.status !== 'live') return;
      void saveRoomCache({
        code, worldId: s.worldId, seed: d.seed, wgv: WORLDGEN_VERSION, lastSeq: s.log.lastSeq,
        lastTerm: s.log.lastTerm, bounds: s.log.bounds, savedAt: Date.now(),
      });
    }, 5000);
  }

  /**
   * Join a room. `auto` is the shared-world mode (join whoever answers within
   * `discoverMs`, host alone otherwise); `host` re-hosts a cached room with
   * its edit log. Transport failures degrade to an offline solo link.
   */
  async function open(roomCode: string, mode: 'auto' | 'host'): Promise<void> {
    if (session || opening) return;
    opening = true;
    try {
      code = roomCode;
      onStatus('connecting');
      const id = await whoami();
      const cache = await loadRoomCache(roomCode);
      // a page that could not reach the room's world server holds a world of its
      // own: it must not join players who are on the server's
      const cut = worldBoot !== null && worldBoot.mode !== 'server';
      if (cut) chat.add('', `World server unavailable (${worldBoot?.detail ?? 'unreachable'}). Playing offline: nothing here is shared or saved to the room.`, true);
      const link = await connectTransport(cut ? [] : transportCandidates(roomCode), { onNote: (m) => { if (!cut) chat.add('', m, true); } });
      transportLabel = link.label;
      const create = mode === 'host';
      const discoverMs = link.solo ? OFFLINE_DISCOVER_MS : ROOM_DISCOVER_MS;
      console.info(`[net] identity ${id.id} -> room ${roomCode} over ${link.label} (${create ? 'host' : 'auto'}, discover ${discoverMs}ms)`);
      const s = new NetSession(id, link.t, bridge, {
        name: readName() ?? keyName, code: roomCode, create, worldId: pageLink?.hello?.meta?.worldId || cache?.worldId || '',
        // offline solo model only: both host paths continue the room's cached log
        resume: cache ? { lastSeq: cache.lastSeq, lastTerm: cache.lastTerm, bounds: cache.bounds } : undefined,
        discoverMs,
        link: pageLink,
      }, {
        chat: (n, text, sys) => { chat.add(n, text, sys); if (n && !sys) d.onPeerChat?.(n, text); },
        status: onStatus,
        members: () => refreshStatus(),
        hostChanged: () => refreshStatus(),
        kicked: (reason) => chat.add('', reason, true),
        mobUsed: (r, fx) => d.onMobUsed?.(r, fx),
      });
      session = s;
      bridge.session = s;
      onStatus(s.status);
      stopTimer = workerTimer(() => { bridge.tick(); s.tick(); }, 50);
      startCacheTimer(s);
    } finally {
      opening = false;
    }
  }

  function close(): void {
    clearInterval(cacheTimer);
    stopTimer?.();
    stopTimer = null;
    session?.leave();
    session = null;
    bridge.dispose();
    remote.clear();
    chat.setStatus(null);
    listSig = '';
  }

  /** Title "Play": join this page's room right away. */
  function play(): void {
    if (session || opening) return;
    void open(room, 'auto').catch((e) => {
      chat.add('', `Could not join ${room}: ${e instanceof Error ? e.message : String(e)}`, true);
      onStatus('closed');
    });
  }

  // --------------------------------------------------------- player list

  /** Tab, or the pause menu (the pointer is unlocked there, so no key holder). */
  function refreshPlayers(force = false): void {
    const rows = session && session.status !== 'closed' ? session.memberList() : [];
    const want = !screen.open && rows.length > 0 && (tabHeld || (d.menus.open && d.menus.currentScreen === 'pause'));
    if (!want) {
      if (listSig) { listSig = ''; chat.showPlayers(null); }
      return;
    }
    const list = rows.map((m) => ({ name: m.name + (m.isMe ? ' (you)' : ''), ping: m.ping, isHost: m.isHost }));
    const sig = list.map((r) => `${r.name}${r.isHost ? '*' : ''}${Math.round(r.ping / 5)}`).join('|');
    const now = performance.now();
    if (!force && sig === listSig && now - listAt < 1000) return;
    listSig = sig;
    listAt = now;
    chat.showPlayers(list);
  }

  // ------------------------------------------------------------- wiring

  d.menus.onPlayWorld = play;

  const ctl: MpController = {
    inSession: () => session !== null && session.status !== 'closed',
    code: () => code,
    roomUrl: () => { const u = new URL(location.href); u.search = '?room=' + code; u.hash = ''; return u.toString(); },
    statusText: () => (session
      ? (session.srv
        ? `Online (${session.memberList().length}) - world saved on the server`
        : `${roleLabel(session)} (${session.memberList().length}) - term ${session.term}`)
      : statusText),
    isHost: () => session?.isHost ?? false,
    players: () => session?.memberList() ?? [],
    transportLabel: () => transportLabel,
    /** Joining another room rebinds the save namespace, so it reloads. */
    join(roomCode, name) {
      rememberName(name);
      try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ mode: 'join', code: roomCode, name })); } catch { /* ignore */ }
      d.save.flush(true);
      location.reload();
    },
    rehost(roomCode, name) {
      rememberName(name);
      try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ mode: 'host', code: roomCode, name })); } catch { /* ignore */ }
      d.save.flush(true);
      location.reload();
    },
    leave() {
      close();
      try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
      d.save.flush(true);
      location.reload();
    },
    kick: (id) => session?.kick(id),
    cachedRooms: async () => (await listRoomCaches()).filter((r) => r.seed === d.seed && r.wgv === WORLDGEN_VERSION),
  };

  const screen = new MultiplayerScreen(document.body, d.guiScale, ctl);
  d.menus.onMultiplayer = () => screen.show();

  chat.onSend = (text) => session?.chat(text);

  const locked = (): boolean => document.pointerLockElement === d.canvas;
  window.addEventListener('keydown', (e) => {
    if (screen.open) {
      if (screen.handleKey(e)) { e.preventDefault(); e.stopImmediatePropagation(); }
      return;
    }
    if (!session || chat.open) return;
    if (e.code === 'Tab') {
      e.preventDefault();
      e.stopImmediatePropagation();
      tabHeld = true;
      refreshPlayers(true);
      return;
    }
    if (!locked()) return;
    if ((e.code === 'KeyT' || e.code === 'Slash') && !e.repeat) {
      // multiplayer: T is chat (single player keeps T = structure teleport)
      e.preventDefault();
      e.stopImmediatePropagation();
      chat.show();
    }
  }, true);
  window.addEventListener('keyup', (e) => {
    if (e.code !== 'Tab') return;
    tabHeld = false;
    refreshPlayers(true);
  }, true);

  // a page that came back from the Multiplayer screen joins its room at once
  if (boot) {
    const b = boot;
    void (async () => {
      try {
        await open(b.code, b.mode === 'host' ? 'host' : 'auto');
        d.menus.onPlay?.(null);
      } catch (e) {
        chat.add('', 'Multiplayer failed: ' + (e instanceof Error ? e.message : String(e)), true);
      }
    })();
  } else {
    // warm the identity up front: Play then starts instantly and the Options
    // name field already shows Player#### before the first click
    void whoami().catch(() => {});
  }

  if (import.meta.env?.DEV || /[?&]netdebug/.test(location.search)) {
    (window as unknown as { __net?: unknown }).__net = {
      get session() { return session; }, bridge, ctl, chunks: d.chunks, player: d.player, entities: d.entities, room,
    };
  }
  return {
    get session() { return session; },
    useOnMob(mob, held) {
      const id = session && !session.isHost ? bridge.remoteMobId(mob) : null;
      return id === null || !session ? null : session.useMob(id, held);
    },
    frame() {
      remote.update(session && session.status !== 'closed' ? session : null);
      chat.update();
      refreshPlayers();
    },
  };
}
